import { OvFormInput, PageWrapper } from '@components'
import { Box, Button, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { useStore } from '@hooks/useStore'
import { OvFormProvider } from 'src/components/core/ov-form-provider/ov-form-provider'
import { OvForm } from 'src/components/core/ov-form/ov-form'
import { TOOLTIPS, URL_PATHS } from '@constants'

interface IForgotPasswordForm {
  email: string
}

export const ForgotPasswordView = observer(() => {
  const { auth } = useStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const form = useForm<IForgotPasswordForm>({
    defaultValues: {
      email: '',
    },
  })

  const onSubmit = async (data: IForgotPasswordForm) => {
    setIsSubmitting(true)
    try {
      await auth.forgotPassword(data.email)
      setEmailSent(true)
      // Note: Backend always returns success to prevent email enumeration
    } catch (error: any) {
      // Even on error, show success message for security (prevent email enumeration)
      setEmailSent(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = () => {
    form.handleSubmit(onSubmit)()
  }

  return (
    <PageWrapper background="primary-gradient">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: '1 0 auto',
        }}>
        <Card sx={{ maxWidth: 450, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            {emailSent ? (
              <Stack spacing={3}>
                <Typography
                  variant="h4"
                  align="center">
                  E-mail Verzonden!
                </Typography>
                <Typography
                  variant="body1"
                  align="center">
                  Als je account bestaat, sturen we je een mail met een resetlink. Check je inbox en volg de
                  instructies om je wachtwoord te resetten.
                </Typography>
                <Tooltip title={TOOLTIPS.BACK_TO_LOGIN}>
                  <Button
                    component={Link}
                    to={URL_PATHS.LOGIN}
                    variant="contained"
                    fullWidth>
                    Terug naar inloggen
                  </Button>
                </Tooltip>
              </Stack>
            ) : (
              <OvFormProvider
                form={form}
                onSubmit={handleSubmit}
                loading={isSubmitting}
                enableNavigationBlocker={false}>
                <OvForm>
                  <Stack spacing={3}>
                    <Typography
                      variant="h4"
                      align="center">
                      Wachtwoord Vergeten
                    </Typography>
                    <Typography
                      variant="body2"
                      align="center"
                      color="text.secondary">
                      Vul je e-mailadres in om een nieuw wachtwoord aan te vragen.
                    </Typography>

                    <OvFormInput<IForgotPasswordForm>
                      name="email"
                      label="E-mailadres"
                      type="email"
                      rules={{
                        required: 'E-mailadres is verplicht',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Ongeldig e-mailadres',
                        },
                      }}
                    />

                    <Tooltip title={TOOLTIPS.SEND_RESET_LINK}>
                      <span>
                        <Button
                          type="submit"
                          variant="contained"
                          fullWidth
                          disabled={isSubmitting}>
                          {isSubmitting ? 'Verzenden...' : 'Reset Link Verzenden'}
                        </Button>
                      </span>
                    </Tooltip>

                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">
                        Wachtwoord toch nog weten?{' '}
                        <Link
                          to={URL_PATHS.LOGIN}
                          style={{ textDecoration: 'none', color: 'primary.main' }}>
                          Terug naar inloggen
                        </Link>
                      </Typography>
                    </Box>
                  </Stack>
                </OvForm>
              </OvFormProvider>
            )}
          </CardContent>
        </Card>
      </Box>
    </PageWrapper>
  )
})
